// src/components/common/SubMenu.tsx

import React from 'react';
import {
  Box,
  ButtonBase,
  FormControl,
  Select,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  alpha,
  type SelectChangeEvent,
} from '@mui/material';

export interface SubMenuItem {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface SubMenuProps {
  items: SubMenuItem[];
  activeItem: string;
  onChange: (value: string) => void;
  /** Content rendered to the right of the tabs (e.g. action buttons) */
  actions?: React.ReactNode;
  /** Content rendered to the left of the tabs (e.g. help button) */
  leftActions?: React.ReactNode;
}

/**
 * SubMenu - Horizontal sub-navigation band
 *
 * Desktop (sm+): Horizontal bar with tab items
 * Mobile (xs): Select dropdown
 */
export const SubMenu: React.FC<SubMenuProps> = ({ items, activeItem, onChange, actions, leftActions }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (items.length <= 1) return null;

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value);
  };

  // Mobile: Select dropdown + actions row
  if (isMobile) {
    const selectDropdown = (
      <FormControl size="small" sx={{ flex: 1 }}>
        <Select
          value={activeItem}
          onChange={handleSelectChange}
          renderValue={(value) => {
            const item = items.find(i => i.value === value);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {item?.icon && React.cloneElement(item.icon as React.ReactElement, { sx: { fontSize: 18, color: 'primary.main' } })}
                <span>{item?.label}</span>
              </Box>
            );
          }}
          sx={{
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              py: 0.75,
            },
          }}
        >
          {items.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.icon && (
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {React.cloneElement(item.icon as React.ReactElement, { sx: { fontSize: 18 } })}
                </ListItemIcon>
              )}
              <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>
                {item.label}
              </ListItemText>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );

    if (!actions && !leftActions) return selectDropdown;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
        {leftActions}
        {selectDropdown}
        {actions && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {actions}
          </Box>
        )}
      </Box>
    );
  }

  // Desktop: Horizontal navigation band with optional actions
  const tabsBar = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.5,
        backgroundColor: 'grey.100',
        borderRadius: '8px',
        borderBottom: '1px solid',
        borderColor: 'divider',
        flex: 1,
        minWidth: 0,
      }}
    >
      {items.map((item) => {
        const isActive = item.value === activeItem;
        return (
          <ButtonBase
            key={item.value}
            onClick={() => onChange(item.value)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 2,
              py: 0.75,
              borderRadius: '6px',
              fontSize: '0.8rem',
              fontWeight: isActive ? 600 : 500,
              whiteSpace: 'nowrap',
              color: isActive ? 'primary.main' : 'text.secondary',
              backgroundColor: isActive ? 'background.paper' : 'transparent',
              boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: isActive ? 'background.paper' : alpha(theme.palette.action.hover, 0.08),
                color: isActive ? 'primary.main' : 'text.primary',
              },
            }}
          >
            {item.icon && React.cloneElement(item.icon as React.ReactElement, {
              sx: { fontSize: 18 },
            })}
            {item.label}
          </ButtonBase>
        );
      })}
    </Box>
  );

  // If no actions/leftActions, render just the tabs bar
  if (!actions && !leftActions) return tabsBar;

  // Wrap with flex row: [leftActions] [tabs] [actions]
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
      {leftActions}
      {tabsBar}
      {actions && (
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {actions}
        </Box>
      )}
    </Box>
  );
};

export default SubMenu;
